import { useState } from "react";
import { useSearchParams } from "react-router-dom";
import { ChevronDown, ChevronRight } from "lucide-react";
import { useProjects } from "../lib/projects";
import { useErrorLogs, type ErrorLogEntry, type LogsFilters } from "../lib/logs";
import { EmptyState, TableSkeleton } from "../components/states";

// One dropdown for "source/platform": ad platforms filter by `platform`, the
// BigQuery-sync source filters by `source` (its platform is the synthetic
// 'bigquery'). Keeping it one control matches how operators think ("show me
// Meta errors" / "show me BigQuery errors").
const SOURCE_OPTIONS: Array<{ value: string; label: string }> = [
  { value: "", label: "Alle Quellen" },
  { value: "meta", label: "Meta CAPI" },
  { value: "google", label: "Google CAPI" },
  { value: "linkedin", label: "LinkedIn CAPI" },
  { value: "bq_sync", label: "BigQuery-Sync" },
];

function toFilters(project: string | null, sp: string): LogsFilters {
  const f: LogsFilters = {};
  if (project) f.project = parseInt(project, 10);
  if (sp === "bq_sync") f.source = "bq_sync";
  else if (sp) f.platform = sp;
  return f;
}

export function LogsPage() {
  const projectsQuery = useProjects();
  const projects = projectsQuery.data?.items ?? [];

  const [params, setParams] = useSearchParams();
  const projectParam = params.get("project");
  const sourceParam = params.get("src") ?? "";

  const logsQuery = useErrorLogs(toFilters(projectParam, sourceParam));
  const items = logsQuery.data?.items ?? [];

  const setParam = (key: string, value: string | null) => {
    const next = new URLSearchParams(params);
    if (value === null || value === "") next.delete(key);
    else next.set(key, value);
    setParams(next, { replace: true });
  };

  return (
    <div className="px-6 py-8">
      <div className="mx-auto max-w-content space-y-6">
        <header className="flex items-baseline justify-between gap-4">
          <div>
            <p className="font-mono text-xs uppercase tracking-wider text-text-tertiary">
              Logs
            </p>
            <h1 className="mt-1 text-2xl font-semibold tracking-tight text-text-primary">
              Fehler &amp; Errorlogs
            </h1>
          </div>
          <div className="flex items-center gap-2">
            <select
              value={sourceParam}
              onChange={(e) => setParam("src", e.target.value)}
              className="rounded-md border border-border-primary bg-bg-secondary px-3 py-1.5 text-sm font-medium text-text-primary focus:border-accent focus:outline-none"
            >
              {SOURCE_OPTIONS.map((o) => (
                <option key={o.value} value={o.value}>
                  {o.label}
                </option>
              ))}
            </select>
            <select
              value={projectParam ?? ""}
              onChange={(e) => setParam("project", e.target.value || null)}
              className="rounded-md border border-border-primary bg-bg-secondary px-3 py-1.5 text-sm font-medium text-text-primary focus:border-accent focus:outline-none"
            >
              <option value="">Alle Projekte</option>
              {projects.map((p) => (
                <option key={p.id} value={p.id}>
                  {p.name}
                  {!p.active ? " (paused)" : ""}
                </option>
              ))}
            </select>
          </div>
        </header>

        <p className="text-[11px] text-text-tertiary">
          Alle erfassten Fehler aus dem Server-Versand an Werbe-Plattformen (CAPI)
          und dem BigQuery-Sync — neueste zuerst. CAPI-Fehler enthalten die
          Original-Antwort der Plattform (z. B. Metas Fehlercode). Aktualisiert
          sich automatisch.
        </p>

        {logsQuery.isLoading ? (
          <TableSkeleton rows={6} />
        ) : logsQuery.error ? (
          <div className="rounded-md border border-danger bg-danger-bg px-3 py-2 text-sm text-danger">
            {String((logsQuery.error as Error).message)}
          </div>
        ) : items.length === 0 ? (
          <EmptyState
            title="Keine Fehler"
            body={
              projectParam || sourceParam
                ? "Für diesen Filter wurden keine Fehler protokolliert."
                : "Es wurden bisher keine Fehler protokolliert. 🎉"
            }
          />
        ) : (
          <ul className="space-y-1.5">
            {items.map((e, i) => (
              <LogRow key={`${e.source}-${e.eventId ?? "x"}-${e.occurredAt}-${i}`} entry={e} />
            ))}
          </ul>
        )}
      </div>
    </div>
  );
}

const SOURCE_CHIP: Record<string, { text: string; cls: string }> = {
  meta: { text: "Meta", cls: "bg-blue-500/15 text-blue-600 dark:text-blue-400" },
  google: { text: "Google", cls: "bg-green-500/15 text-green-600 dark:text-green-400" },
  linkedin: { text: "LinkedIn", cls: "bg-sky-400/15 text-sky-600 dark:text-sky-300" },
  bigquery: { text: "BigQuery", cls: "bg-amber-500/15 text-amber-600 dark:text-amber-400" },
};

function LogRow({ entry }: { entry: ErrorLogEntry }) {
  const [open, setOpen] = useState(false);
  const chip = SOURCE_CHIP[entry.platform] ?? {
    text: entry.platform,
    cls: "bg-bg-tertiary text-text-tertiary",
  };
  const hasDetail = !!entry.detail || !!entry.eventId;
  return (
    <li className="rounded-md border border-border-primary bg-bg-secondary">
      <button
        type="button"
        onClick={() => hasDetail && setOpen((o) => !o)}
        className={`flex w-full items-center gap-3 px-3 py-2 text-left ${
          hasDetail ? "hover:bg-bg-hover" : "cursor-default"
        }`}
      >
        <span className="w-3 flex-shrink-0 text-text-tertiary">
          {hasDetail ? (open ? <ChevronDown size={12} /> : <ChevronRight size={12} />) : null}
        </span>
        <span
          className={`flex-shrink-0 rounded px-1.5 py-0.5 font-mono text-[9px] font-semibold ${chip.cls}`}
        >
          {chip.text}
        </span>
        <span className="w-40 flex-shrink-0 truncate text-sm text-text-primary">
          {entry.projectName ?? `Projekt ${entry.projectId}`}
        </span>
        <span className="w-28 flex-shrink-0 truncate font-mono text-[11px] text-text-secondary">
          {entry.eventType ?? "—"}
        </span>
        {entry.status != null && (
          <span className="flex-shrink-0 rounded bg-danger/15 px-1.5 py-0.5 font-mono text-[10px] font-semibold text-danger">
            {entry.status}
          </span>
        )}
        <span className="flex-1 truncate font-mono text-[11px] text-text-secondary">
          {entry.message ?? "—"}
        </span>
        <span className="flex-shrink-0 font-mono text-[10px] text-text-tertiary">
          {new Date(entry.occurredAt).toLocaleString("de-DE")}
        </span>
      </button>
      {open && hasDetail && (
        <div className="border-t border-border-primary bg-bg-tertiary p-3 font-mono text-[11px] leading-relaxed text-text-primary">
          {entry.eventId && (
            <div className="mb-1 text-text-secondary">
              event_id: <span className="text-text-primary">{entry.eventId}</span>
            </div>
          )}
          {entry.detail && (
            <pre className="max-h-[40vh] overflow-auto whitespace-pre-wrap break-all">
              {entry.detail}
            </pre>
          )}
        </div>
      )}
    </li>
  );
}
